import React, { useState, useEffect, useCallback } from 'react';
import { getTransactions, getCompanies } from '../services/api';

const formatCurrency = (amount) => {
  if (!amount || amount === 0) return '-';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2,
  }).format(amount);
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const getTransactionBadge = (type, isInternal, isFd) => {
  if (isInternal) return <span className="badge badge-internal">Internal</span>;
  if (isFd) return <span className="badge badge-fd">FD Interest</span>;
  if (type === 'upi_credit' || type === 'upi_debit') return <span className="badge badge-upi">UPI</span>;
  if (type === 'neft') return <span className="badge badge-neft">NEFT</span>;
  if (type === 'imps') return <span className="badge badge-neft">IMPS</span>;
  if (type === 'fd_maturity') return <span className="badge badge-fd">FD Maturity</span>;
  return null;
};

const Transactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Filters
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('2025-12');
  const [selectedType, setSelectedType] = useState('');

  const fetchCompanies = useCallback(async () => {
    try {
      const response = await getCompanies();
      setCompanies(response.data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  const fetchTransactions = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: 20,
      };
      
      if (selectedCompany) params.companyId = selectedCompany;
      if (selectedMonth) params.month = selectedMonth;
      if (selectedType) params.type = selectedType;

      const response = await getTransactions(params);
      setTransactions(response.data.transactions);
      setPagination({
        page: response.data.page,
        totalPages: response.data.totalPages
      });
    } catch (err) {
      setError('Failed to load transactions');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, selectedCompany, selectedMonth, selectedType]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  return (
    <div>
      <div className="filters">
        <div className="filter-group">
          <label>Month:</label>
          <select value={selectedMonth} onChange={(e) => { setSelectedMonth(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}>
            <option value="">All Months</option>
            <option value="2025-12">December 2025</option>
            <option value="2025-11">November 2025</option>
            <option value="2025-10">October 2025</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Company:</label>
          <select value={selectedCompany} onChange={(e) => { setSelectedCompany(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}>
            <option value="">All Companies</option>
            {companies.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Type:</label>
          <select value={selectedType} onChange={(e) => { setSelectedType(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}>
            <option value="">All Types</option>
            <option value="upi_credit">UPI Credit</option>
            <option value="upi_debit">UPI Debit</option>
            <option value="neft">NEFT</option>
            <option value="imps">IMPS</option>
            <option value="internal_transfer">Internal Transfer</option>
            <option value="fd_interest">FD Interest</option>
            <option value="fd_maturity">FD Maturity</option>
          </select>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="loading">
          <div className="spinner"></div>
        </div>
      ) : transactions.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📋</div>
          <p>No transactions found</p>
        </div>
      ) : (
        <>
          <div className="card">
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Company</th>
                    <th>Description</th>
                    <th>Type</th>
                    <th style={{ textAlign: 'right' }}>Credit</th>
                    <th style={{ textAlign: 'right' }}>Debit</th>
                    <th style={{ textAlign: 'right' }}>Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((txn) => (
                    <tr key={txn.id}>
                      <td>{formatDate(txn.date)}</td>
                      <td>
                        <span className="company-code" style={{ marginLeft: 0 }}>
                          {txn.company_code}
                        </span>
                      </td>
                      <td style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {txn.description}
                      </td>
                      <td>
                        {getTransactionBadge(txn.transaction_type, txn.is_internal_transfer, txn.is_fd_interest)}
                      </td>
                      <td style={{ textAlign: 'right' }} className="amount-credit">
                        {formatCurrency(txn.credit)}
                      </td>
                      <td style={{ textAlign: 'right' }} className="amount-debit">
                        {formatCurrency(txn.debit)}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {formatCurrency(txn.balance)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="pagination">
            <button 
              disabled={pagination.page <= 1}
              onClick={() => handlePageChange(pagination.page - 1)}
            >
              Previous
            </button>
            <span style={{ padding: '8px 16px' }}>
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <button 
              disabled={pagination.page >= pagination.totalPages}
              onClick={() => handlePageChange(pagination.page + 1)}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default Transactions;
